/**
 * Хук настройки страницы-списка (АН-09): текущая настройка вида, сохранённые виды, запоминание выбора и
 * постраничная подгрузка строк. Любая страница-список подключает его одной строкой:
 *   const lv = useListView(source); <ListViewBar lv={lv} /> <DataView list={lv.list} config={lv.cleaned} rows={lv.rows} />
 */
import type { ListResponse, ListView, SourceInfo, ViewConfig, ViewPref } from '@epc/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { cleanConfig, configKey, defaultConfigFor, sameConfig } from './util';

export const sourcesQuery = {
  queryKey: ['reports', 'sources'],
  queryFn: () => api<SourceInfo[]>('/reports/sources'),
  staleTime: 5 * 60_000,
};

export const PAGE_SIZE = 50;

export function useListView(src: SourceInfo | undefined, opts: { pageSize?: number } = {}) {
  const qc = useQueryClient();
  const page = src?.key ?? '';
  const pageSize = opts.pageSize ?? PAGE_SIZE;
  const [config, setConfigState] = useState<ViewConfig | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<string>('');
  const restored = useRef<string | null>(null);
  const lastSaved = useRef('');

  const views = useQuery({
    queryKey: ['reports', 'views', page],
    queryFn: () => api<ListView[]>('/reports/views', { query: { page } }),
    enabled: !!src,
  });
  const pref = useQuery({
    queryKey: ['reports', 'pref', page],
    queryFn: () => api<ViewPref>(`/reports/prefs/${page}`),
    enabled: !!src,
  });

  // Один раз на источник: вернуть то, что человек выбирал в прошлый раз
  useEffect(() => {
    if (!src || !pref.data || !views.data || restored.current === src.key) return;
    restored.current = src.key;
    const picked = views.data.find((v) => v.id === pref.data?.viewId) ?? null;
    const restoredConfig = pref.data.config ?? picked?.config ?? defaultConfigFor(src);
    lastSaved.current = `${picked?.id ?? ''}|${configKey(restoredConfig)}`;
    setViewId(picked?.id ?? null);
    setConfigState(restoredConfig);
  }, [src, pref.data, views.data]);

  // Смена источника на той же странице начинает с чистого листа
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс только при смене ключа источника
  useEffect(() => {
    setConfigState(null);
    setViewId(null);
    restored.current = null;
  }, [page]);

  const effective = config ?? (src ? defaultConfigFor(src) : null);
  const cleaned = useMemo(() => (effective && src ? cleanConfig(effective, src) : null), [effective, src]);
  const key = cleaned ? configKey(cleaned) : '';

  // Запоминание выбора: с небольшой задержкой, чтобы не писать на каждое нажатие
  const savePref = useMutation({
    mutationFn: (body: { viewId: string | null; config: ViewConfig }) =>
      api<ViewPref>(`/reports/prefs/${page}`, { method: 'PUT', body }),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: savePref.mutate не нужен в зависимостях: запись только при смене вида
  useEffect(() => {
    if (!src || !config || restored.current !== src.key) return;
    const stamp = `${viewId ?? ''}|${configKey(config)}`;
    if (stamp === lastSaved.current) return;
    const t = setTimeout(() => {
      lastSaved.current = stamp;
      savePref.mutate({ viewId, config });
    }, 700);
    return () => clearTimeout(t);
  }, [src, config, viewId]);

  const rows = useInfiniteQuery({
    queryKey: ['reports', 'rows', page, key, asOf, pageSize],
    enabled: !!src && !!cleaned,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api<ListResponse>(`/reports/sources/${page}/rows`, {
        query: { view: key, page: pageParam, pageSize, asOf: asOf || undefined },
      }),
    getNextPageParam: (last, all) => {
      if (last.groups) return undefined;
      const loaded = all.reduce((n, p) => n + p.rows.length, 0);
      return loaded < last.total ? all.length + 1 : undefined;
    },
    placeholderData: (prev) => prev,
  });

  const first = rows.data?.pages[0];
  const allRows = useMemo(() => rows.data?.pages.flatMap((p) => p.rows) ?? [], [rows.data]);
  const current = views.data?.find((v) => v.id === viewId) ?? null;
  const base = current?.config ?? (src ? defaultConfigFor(src) : null);
  const dirty = !!effective && !!base && !sameConfig(cleanConfig(effective, src), cleanConfig(base, src));

  const invalidateViews = () => qc.invalidateQueries({ queryKey: ['reports', 'views', page] });
  const create = useMutation({
    mutationFn: (v: { name: string; shared: boolean }) =>
      api<ListView>('/reports/views', {
        method: 'POST',
        body: { page, name: v.name, shared: v.shared, config: cleaned },
      }),
    onSuccess: (v) => {
      setViewId(v.id);
      void invalidateViews();
    },
  });
  const update = useMutation({
    mutationFn: (v: { id: string; name?: string; shared?: boolean; withConfig?: boolean }) =>
      api<ListView>(`/reports/views/${v.id}`, {
        method: 'PATCH',
        body: { name: v.name, shared: v.shared, config: v.withConfig ? cleaned : undefined },
      }),
    onSuccess: () => void invalidateViews(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/reports/views/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      if (viewId === id) setViewId(null);
      void invalidateViews();
    },
  });

  return {
    src,
    config: effective,
    cleaned,
    setConfig: (c: ViewConfig) => setConfigState(c),
    viewId,
    current,
    views: views.data ?? [],
    selectView: (id: string | null) => {
      setViewId(id);
      const v = views.data?.find((x) => x.id === id);
      setConfigState(v ? v.config : src ? defaultConfigFor(src) : null);
    },
    reset: () => {
      setViewId(null);
      setConfigState(src ? defaultConfigFor(src) : null);
    },
    dirty,
    asOf,
    setAsOf,
    list: first ?? null,
    rows: allRows,
    isLoading: !!src && (rows.isPending || pref.isPending || views.isPending),
    isFetching: rows.isFetching,
    error: rows.error ?? pref.error ?? views.error,
    hasNext: rows.hasNextPage,
    fetchNext: () => void rows.fetchNextPage(),
    fetchingNext: rows.isFetchingNextPage,
    create,
    update,
    remove,
  };
}

export type ListViewState = ReturnType<typeof useListView>;
